import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Upload,
  FileBarChart2,
  Settings,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  LogOut,
  Wrench,
  Repeat,
  MessageSquare,
  Pin,
  PinOff,
  SlidersHorizontal,
} from 'lucide-react';
import { ImpersonationBanner } from './ImpersonationBanner';

// Service module navigation tree
const navTree = [
  { id: 'dashboard', label: 'Dashboard', path: '/service/dashboard', icon: LayoutDashboard },
  { id: 'jobs', label: 'All Service Jobs', path: '/service/jobs', icon: ClipboardList },
  { id: 'upload', label: 'Upload Data', path: '/service/upload', icon: Upload },
  { id: 'templates', label: 'Templates', path: '/service/templates', icon: MessageSquare },
  {
    id: 'reports',
    label: 'Reports',
    icon: FileBarChart2,
    children: [
      { id: 'rep-in', label: 'Vehicle Inwards', path: '/service/reports/vehicle-inwards' },
      { id: 'rep-out', label: 'Vehicle Outwards', path: '/service/reports/vehicle-outwards' },
      { id: 'rep-tech', label: 'Technician Wise', path: '/service/reports/technician' },
      { id: 'rep-status', label: 'Job Status', path: '/service/reports/job-status' },
      { id: 'rep-sh', label: 'Service Head', path: '/service/reports/service-head' },
      { id: 'rep-jt', label: 'Job Type', path: '/service/reports/job-type' },
      { id: 'rep-sup', label: 'Supervisor Wise', path: '/service/reports/supervisor' },
      { id: 'rep-recon', label: 'Reconciliation', path: '/service/reports/reconciliation' },
      { id: 'rep-multi', label: 'Multi Reports', path: '/service/reports/multi-reports' },
    ],
  },
  { id: 'masters',
    label: 'Masters',
    icon: Settings,
    children: [
      { id: 'm-status', label: 'Status', path: '/service/masters/status' },
      { id: 'm-service-head', label: 'Service Head', path: '/service/masters/service-head' },
      { id: 'm-job-cat', label: 'Job Category', path: '/service/masters/job-category' },
      { id: 'm-job-type', label: 'Job Type', path: '/service/masters/job-type' },
      { id: 'm-cust-type', label: 'Customer Type', path: '/service/masters/customer-type' },
      { id: 'm-supervisor', label: 'Supervisor', path: '/service/masters/supervisor' },
      { id: 'm-tech', label: 'Technician', path: '/service/masters/technician' },
    ],
  },
  { id: 'settings', label: 'Settings', path: '/service/settings', icon: SlidersHorizontal },
];

export function ServiceLayout({ children, user, onLogout, title, subtitle, actions }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-hide drawer state
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  // Desktop pin — persisted in localStorage. When pinned, drawer stays open and main content shifts right.
  const [pinned, setPinned] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('service.sidebar.pinned') === '1';
  });
  // Track if viewport is desktop (>=768px) so pin only applies there
  const [isDesktop, setIsDesktop] = React.useState(() => (
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 768px)').matches
  ));

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  React.useEffect(() => {
    window.localStorage.setItem('service.sidebar.pinned', pinned ? '1' : '0');
  }, [pinned]);

  // When viewport drops below desktop while pinned, close the drawer (mobile must be overlay-only)
  React.useEffect(() => {
    if (!isDesktop && pinned) setDrawerOpen(false);
  }, [isDesktop, pinned]);

  // Auto-expand the parent that contains the active route
  const initialOpen = React.useMemo(() => {
    const set = new Set();
    navTree.forEach((item) => {
      if (item.children?.some((c) => location.pathname.startsWith(c.path))) set.add(item.id);
    });
    return set;
  }, [location.pathname]);
  const [openIds, setOpenIds] = React.useState(initialOpen);

  // Close drawer on Esc (when not pinned on desktop)
  React.useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Lock body scroll when overlay drawer is open (and not pinned/desktop)
  React.useEffect(() => {
    const isOverlay = drawerOpen && !(pinned && isDesktop);
    if (isOverlay) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen, pinned, isDesktop]);

  const toggleGroup = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // When pinned on desktop, keep drawer open
  const drawerVisible = drawerOpen || (pinned && isDesktop);
  const isPinnedDesktop = pinned && isDesktop && drawerVisible;

  const handleNavigate = (path) => {
    navigate(path);
    if (!isPinnedDesktop) setDrawerOpen(false);
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage:
          'radial-gradient(60rem 40rem at 0% 0%, rgba(199,210,254,0.45) 0%, transparent 60%), radial-gradient(60rem 40rem at 100% 0%, rgba(252,165,165,0.25) 0%, transparent 60%), linear-gradient(180deg, #F8FAFF 0%, #FAF5FF 60%, #FFF5F5 100%)',
      }}
    >
      <ImpersonationBanner />

      {/* Drawer Backdrop — only when overlay (not pinned-desktop) */}
      {drawerVisible && !isPinnedDesktop && (
        <div
          data-testid="service-drawer-backdrop"
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Drawer Sidebar */}
      <aside
        data-testid="service-sidebar"
        className={`fixed top-0 bottom-0 left-0 z-[51] w-[280px] flex flex-col transition-transform duration-300 ease-out
          ${drawerVisible ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="absolute inset-0 backdrop-blur-2xl bg-white/85 border-r border-white/40 shadow-[0_8px_40px_rgba(15,23,42,0.12)]" />
        <div className="relative flex flex-col h-full">
          {/* Brand + Close + Pin */}
          <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-slate-200/60">
            <button
              onClick={() => handleNavigate('/modules')}
              data-testid="service-sidebar-brand"
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md shrink-0 text-white"
              style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}
            >
              <Wrench size={18} />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold tracking-tight text-slate-900 truncate">Service</p>
              <p className="text-[11px] text-slate-500 truncate">AutoBook Pro</p>
            </div>
            {isDesktop && (
              <button
                onClick={() => setPinned((p) => !p)}
                data-testid="service-sidebar-pin"
                title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                className={`p-1.5 rounded-lg transition-all ${pinned ? 'text-indigo-700 bg-indigo-50' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}
              >
                {pinned ? <PinOff size={14} /> : <Pin size={14} />}
              </button>
            )}
            {!isPinnedDesktop && (
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="service-sidebar-close"
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
            {navTree.map((item) => {
              const Icon = item.icon;
              const hasChildren = !!item.children?.length;
              const active = item.path && isActive(item.path);
              const open = openIds.has(item.id);
              const childActive = hasChildren && item.children.some((c) => isActive(c.path));

              if (!hasChildren) {
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.path)}
                    data-testid={`service-nav-${item.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                      ${active
                        ? 'bg-gradient-to-r from-indigo-500/12 via-violet-500/10 to-fuchsia-500/8 text-indigo-700 shadow-sm ring-1 ring-indigo-200/60'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'}
                    `}
                  >
                    <Icon size={18} className="shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              }

              return (
                <div key={item.id}>
                  <button
                    onClick={() => toggleGroup(item.id)}
                    data-testid={`service-nav-group-${item.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                      ${childActive
                        ? 'text-indigo-700 bg-white/60 ring-1 ring-indigo-200/60'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'}
                    `}
                  >
                    <Icon size={18} className="shrink-0" />
                    <span className="flex-1 text-left truncate">{item.label}</span>
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  {open && (
                    <div className="mt-0.5 ml-3 pl-4 border-l border-slate-200/70 space-y-0.5">
                      {item.children.map((c) => {
                        const sub = isActive(c.path);
                        return (
                          <button
                            key={c.id}
                            onClick={() => handleNavigate(c.path)}
                            data-testid={`service-nav-${c.id}`}
                            className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all
                              ${sub
                                ? 'text-indigo-700 bg-indigo-50/80 font-medium'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'}`}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-2.5 py-3 border-t border-slate-200/60 space-y-1">
            <button
              onClick={() => handleNavigate('/modules')}
              data-testid="service-switch-module-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-600 hover:text-slate-900 hover:bg-white/70 transition-all"
            >
              <Repeat size={18} />
              <span>Switch Module</span>
            </button>
            <button
              onClick={onLogout}
              data-testid="service-logout-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-rose-600 hover:bg-rose-50 transition-all"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main column — shifted right when pinned-desktop */}
      <div
        className="min-h-screen transition-[padding] duration-300 ease-out"
        style={{ paddingLeft: isPinnedDesktop ? 280 : 0 }}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/55 border-b border-white/50">
          <div className="flex items-center gap-3 px-4 md:px-8 py-3">
            <button
              onClick={() => setDrawerOpen((o) => !o)}
              data-testid="service-menu-toggle"
              title="Open menu"
              className="p-2 rounded-lg bg-white/70 border border-white/50 hover:bg-white text-slate-700"
            >
              <Menu size={18} />
            </button>
            <div className="min-w-0 flex-1">
              {title && (
                <h1
                  className="text-[20px] md:text-[22px] font-semibold tracking-tight text-slate-900 truncate"
                  style={{ fontFamily: 'Inter, "SF Pro Text", system-ui, sans-serif' }}
                >
                  {title}
                </h1>
              )}
              {subtitle && <p className="text-[12.5px] text-slate-500 truncate">{subtitle}</p>}
            </div>
            {actions}
            <div className="hidden md:flex items-center gap-3 pl-3 ml-1 border-l border-slate-200">
              <div className="text-right">
                <p className="text-[13px] font-medium text-slate-800 leading-tight">{user?.name || 'User'}</p>
                <p className="text-[11px] text-slate-500">{user?.email}</p>
              </div>
              <div
                className="w-9 h-9 rounded-full text-white flex items-center justify-center text-sm font-semibold shadow-sm"
                style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #DB2777 100%)' }}
              >
                {(user?.name || 'U').charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="px-4 md:px-8 py-6 md:py-8">{children}</main>
      </div>
    </div>
  );
}

export default ServiceLayout;
